import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import useLogout from '@/components/Authorize/hooks';
import { invalidateUser } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import Verification from '@/routes/emailVerify/Verification';

import speedyFlowSlice from '../redux/slice';

const { advanceSpeedyFlow } = speedyFlowSlice.actions;

/**
 * The email verification step. Renders the same component the `/emailVerification` page does —
 * it was already prop-driven, so it needed no extraction, only the resend cooldown it now has.
 *
 * The page redirects on success; here, verification is just one more edge for the machine.
 */
const VerifyEmailStep = () => {
  const dispatch = useDispatch();
  const email = useSelector((state) => state.modals.speedyFlow.email);
  const [emailVerified, setEmailVerified] = useState(false);
  const queryClient = useQueryClient();
  /**
   * Abandon the account made against a mistyped address, then go back to the form.
   *
   * The step moves *on success*, not alongside it. Moving first leaves a window where the session
   * outlives the machine's belief in it, and the flow reads that as an account it was never told
   * about — the same signal a magic link opened in another tab produces — and routes the visitor
   * straight back out of the form they just asked for. If the sign-out fails they stay here, which
   * is the truth: nothing was abandoned.
   *
   * Nothing here has to ask to stay put: `useLogout` never navigates, so the modal — which lives in
   * redux rather than in the route — survives the sign-out and carries on to the sign-up step. The
   * saved destination survives too, because the Forget spares it while the flow is on screen: a
   * logout inside a journey is not the visitor giving up on where they were trying to get to. See
   * `shared/forgetUser`.
   */
  const abandonAccount = useLogout(() => dispatch(advanceSpeedyFlow({ type: 'choseSignUp' })));

  return (
    <div className="flex flex-col p-6" data-component="speedy-step" data-step="verify-email">
      {/*
        `Verification` takes a single prop literally named `props` and destructures the rest out of
        it. Spreading them at the top level leaves that object undefined and the component throws
        on first render.
      */}
      <Verification
        props={{
          emailVerified,
          setEmailVerified: (verified: boolean) => {
            setEmailVerified(verified);
            if (verified) {
              dispatch(advanceSpeedyFlow({ type: 'emailVerified' }));
              // The machine moves on its own event, but the rest of the app learns who the visitor
              // is from the session query — and nothing here had told it. Redux went on saying the
              // address was unconfirmed, so the next page to ask would have asked again.
              invalidateUser(queryClient);
            }
          },
          email,
          // Figma 8306-14454: a mistyped address is otherwise a dead end — the code can never
          // arrive. Sends them back to the form with the flow intact.
          //
          // And signs them out on the way. Signing up created the account and the session before
          // the address was confirmed, so without this they walk back to the form still
          // authenticated as the account they are abandoning — the header saying they are signed
          // in, under the very address they came here to correct.
          onUseDifferentEmail: abandonAccount,
        }}
      />
    </div>
  );
};

export default VerifyEmailStep;
